import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Popover,
  Grid,
  TextField,
  InputAdornment,
  Tooltip
} from '@mui/material'
import { alpha, styled, useTheme } from '@mui/material/styles'

interface ColorPickerProps {
  value: string
  onChange: (color: string) => void
  presetColors?: string[]
  label?: string
}

const ColorSwatch = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected'
})<{ color: string; isSelected: boolean }>(({ theme, color, isSelected }) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: color,
  border: `3px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
    borderColor: theme.palette.primary.main,
  },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

const CustomColorInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}))

const defaultPresetColors = [
  '#f97415', // SEVV Primary Orange
  '#ffa047', // SEVV Primary Light
  '#ffce80', // SEVV Primary Lighter
  '#27ae60', // SEVV Success Green
  '#f39c12', // SEVV Warning Orange
  '#e74c3c', // SEVV Error Red
  '#3498db', // SEVV Info Blue
  '#3f51b5', // Indigo
  '#2196f3', // Blue
  '#03a9f4', // Light Blue
  '#00bcd4', // Cyan
  '#009688', // Teal
  '#4caf50', // Green
  '#8bc34a', // Light Green
  '#cddc39', // Lime
  '#ffeb3b', // Yellow
  '#ff9800', // Orange
  '#ff5722', // Deep Orange
  '#f44336', // Red
  '#e91e63', // Pink
  '#9c27b0', // Purple
  '#673ab7', // Deep Purple
  '#607d8b', // Blue Grey
  '#795548', // Brown
  '#9e9e9e', // Grey
  '#000000', // Black
  '#ffffff', // White
]

export const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  presetColors = defaultPresetColors,
  label = 'Color'
}) => {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [customColor, setCustomColor] = useState(value)

  useEffect(() => {
    setCustomColor(value)
  }, [value])

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleColorSelect = (color: string) => {
    onChange(color)
    setCustomColor(color)
    handleClose()
  }

  const handleCustomColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = event.target.value
    setCustomColor(newColor)
    
    // Validate hex color format
    if (/^#[0-9A-F]{6}$/i.test(newColor)) {
      onChange(newColor)
    }
  }

  const open = Boolean(anchorEl)
  const id = open ? 'color-picker-popover' : undefined

  return (
    <Box>
      <Typography 
        variant="subtitle2" 
        gutterBottom 
        sx={{ 
          color: 'text.primary',
          fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
          fontWeight: 700
        }}
      >
        {label}
      </Typography>
    
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Click to open color picker">
          <IconButton
            onClick={handleClick}
            sx={{
              p: 0,
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          >
            <ColorSwatch
              color={value}
              isSelected={false}
              sx={{
                width: 40,
                height: 40,
                border: '2px solid',
                borderColor: 'divider',
              }}
            />
          </IconButton>
        </Tooltip>

        <CustomColorInput
          size="small"
          value={customColor}
          onChange={handleCustomColorChange}
          placeholder="#000000"
          InputProps={{
            startAdornment: <InputAdornment position="start">#</InputAdornment>,
          }}
          sx={{ 
            width: 120,
            '& .MuiOutlinedInput-root': {
              color: 'text.primary',
              fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
              '& fieldset': {
                borderColor: 'divider',
              },
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
            '& .MuiInputAdornment-root': {
              color: 'text.secondary',
            },
          }}
        />
      </Box>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            p: 3,
            minWidth: 280,
            maxWidth: 320,
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            boxShadow: theme.shadows[8],
          },
        }}
      >
        <Typography 
          variant="subtitle2" 
          gutterBottom 
          sx={{ 
            color: 'text.primary',
            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
            fontWeight: 700
          }}
        >
          Preset Colors
        </Typography>
        
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {presetColors.map((color) => (
            <Grid item key={color}>
              <Tooltip title={color}>
                <ColorSwatch
                  color={color}
                  isSelected={value === color}
                  onClick={() => handleColorSelect(color)}
                />
              </Tooltip>
            </Grid>
          ))}
        </Grid>

        <Typography 
          variant="subtitle2" 
          gutterBottom 
          sx={{ 
            color: 'text.primary',
            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
            fontWeight: 700
          }}
        >
          Custom Color
        </Typography>
        
        <CustomColorInput
          fullWidth
          size="small"
          value={customColor}
          onChange={handleCustomColorChange}
          placeholder="#000000"
          InputProps={{
            startAdornment: <InputAdornment position="start">#</InputAdornment>,
          }}
          helperText="Enter a 6-digit hex color code"
          sx={{
            '& .MuiOutlinedInput-root': {
              color: 'text.primary',
              fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
              '& fieldset': {
                borderColor: 'divider',
              },
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
            '& .MuiInputAdornment-root': {
              color: 'text.secondary',
            },
            '& .MuiFormHelperText-root': {
              color: 'text.disabled',
              fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
            },
          }}
        />
      </Popover>
    </Box>
  )
} 